import { useMemo } from "react";
import { type CommunicationKind, useGetCommunications } from "../../api/useGetCommunications";
import { flattenUniquePages, getId } from "../../../../utils/pagination";

type Props = {
  subTopicId?: string;
  providerId?: string;
  kind?: CommunicationKind;
};

const EMPTY_STATES: Record<CommunicationKind, { title: string; subtitle: string }> = {
  communications: {
    title: 'Aucune communication',
    subtitle: 'Les appels, e-mails et relances enregistrés apparaîtront ici, du plus récent au plus ancien.',
  },
  others: {
    title: 'Aucune autre action',
    subtitle: 'Les actions enregistrées avec « Autre action » apparaîtront ici, de la plus récente à la plus ancienne.',
  },
};

const LOADING_LABELS: Record<CommunicationKind, string> = {
  communications: 'Chargement des communications',
  others: 'Chargement des autres actions',
};

const ERROR_LABELS: Record<CommunicationKind, string> = {
  communications: "Les communications n'ont pas pu être chargées. Rechargez la page pour réessayer.",
  others: "Les autres actions n'ont pas pu être chargées. Rechargez la page pour réessayer.",
};

export function useCommunicationsTab(props: Props) {
  const kind = props.kind ?? 'communications';
  const {
    data,
    isLoading,
    error,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
    fetchNextPage,
  } = useGetCommunications({
    subjectId: props.subTopicId,
    providerId: props.providerId,
    kind,
  });

  const communications = useMemo(() => flattenUniquePages(data?.pages, getId), [data]);

  function handleLoadMore() {
    fetchNextPage();
  }

  return {
    communications,
    isLoading,
    error,
    hasMore: Boolean(hasNextPage),
    isLoadingMore: isFetchingNextPage,
    isLoadMoreError: isFetchNextPageError,
    handleLoadMore,
    emptyState: EMPTY_STATES[kind],
    loadingLabel: LOADING_LABELS[kind],
    errorLabel: ERROR_LABELS[kind],
    loadMoreLabel: kind === 'others' ? "Afficher plus d'actions" : 'Afficher plus de communications',
  };
}
